import type { TableColumns } from '@ff-it/ui';
import { EntityScene, UpdateScene, useEntity } from 'components';
import { Navigate, useParams, useNavigate, useRoutes } from 'react-router';
import { Outlet } from 'react-router-dom';
import { Form } from './Form';
import { Nav } from './Nav';
import type { OutletOrServiceCategory } from '../outletOrServiceCategory/types';
import type { OutletOrServiceCategoryProduct } from './types';
import { ReactElement } from 'react';
import { useSessionScope } from 'services';
import type { ActivityType } from 'types';
import type { ProductFormConfig } from './types';
import { firstProductType } from './ProductTypeNav';
import { resolveProductRouteType } from './productRouteScope';
import { ProductCollectionIndex } from './ProductCollectionIndex';
import { ProductActions } from './ProductActions';

export type ProductTypeRouteModule = {
  formConfig?: ProductFormConfig;
  columns?: TableColumns<OutletOrServiceCategoryProduct>;
};

export type ProductTypeRouteResolver = (type: ActivityType) => ProductTypeRouteModule | undefined;

function ScopedProductOutlet({ type }: { type: ActivityType }): ReactElement {
  const { item } = useEntity<OutletOrServiceCategoryProduct>();
  return item.type === type ? <Outlet /> : <Navigate to=".." replace />;
}

type ScopedProductRoutesProps = {
  outletOrServiceCategory: OutletOrServiceCategory;
  resolveTypeModule: ProductTypeRouteResolver;
};

function ScopedProductRoutes({
  outletOrServiceCategory,
  resolveTypeModule,
}: ScopedProductRoutesProps): ReactElement | null {
  const { type: typeParam } = useParams<{ type: string }>();
  const type = resolveProductRouteType(outletOrServiceCategory.type, typeParam);

  if (!type) {
    return <Navigate to=".." replace />;
  }

  return (
    <ProductRoutesForType
      outletOrServiceCategory={outletOrServiceCategory}
      type={type}
      typeModule={resolveTypeModule(type)}
    />
  );
}

type ProductRoutesForTypeProps = {
  outletOrServiceCategory: OutletOrServiceCategory;
  type: ActivityType;
  typeModule?: ProductTypeRouteModule;
};

function ProductRoutesForType({
  outletOrServiceCategory,
  type,
  typeModule,
}: ProductRoutesForTypeProps): ReactElement | null {
  const { slug } = useSessionScope();
  const navigate = useNavigate();
  const routes = useRoutes([
    {
      index: true,
      element: (
        <ProductCollectionIndex
          outletOrServiceCategory={outletOrServiceCategory}
          type={type}
          typeColumns={typeModule?.columns}
          formConfig={typeModule?.formConfig}
        />
      ),
    },
    {
      path: ':id/',
      element: (
        <EntityScene element={<ScopedProductOutlet type={type} />}>
          <Nav />
        </EntityScene>
      ),
      children: [
        {
          index: true,
          element: (
            <UpdateScene
              deletePath={null}
              createPath={null}
              viewDisabled
              heading={null}
              actions={
                <ProductActions
                  type={type}
                  onRemoved={(cascade) => navigate(cascade ? `/${slug}/preferences` : '../')}
                />
              }
              copy={(product) => ({ ...product, id: undefined })}
            >
              <Form
                outletOrServiceCategory={outletOrServiceCategory}
                typeScope={type}
                formConfig={typeModule?.formConfig}
              />
            </UpdateScene>
          ),
        },
      ],
    },
  ]);

  return routes;
}

function ProductTypeIndex({
  outletOrServiceCategory,
}: {
  outletOrServiceCategory: OutletOrServiceCategory;
}): ReactElement {
  return (
    <Navigate
      to={firstProductType(outletOrServiceCategory.type, outletOrServiceCategory.product_counts ?? {})}
      replace
    />
  );
}

/**
 * Product CRUD routes for one outlet or service category. Every child route is
 * scoped to one activity type. Typed outlets/service categories redirect to
 * their fixed type; nullable ones open the first populated type while
 * permanent type tabs provide navigation to every type.
 */
export function useProductRoutes(
  outletOrServiceCategory: OutletOrServiceCategory,
  resolveTypeModule: ProductTypeRouteResolver,
) {
  return useRoutes([
    {
      index: true,
      element: <ProductTypeIndex outletOrServiceCategory={outletOrServiceCategory} />,
    },
    {
      path: ':type/*',
      element: (
        <ScopedProductRoutes outletOrServiceCategory={outletOrServiceCategory} resolveTypeModule={resolveTypeModule} />
      ),
    },
  ]);
}
